import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Card,
  ErrorState,
  FormStack,
  LoadingState,
  Page,
  Select,
  Text,
} from "@marekl/adminer/components";
import { apiErrorMessage, notifyError, notifySuccess } from "@marekl/adminer/api";
import { useConfirm, useT } from "@marekl/adminer/hooks";
import { AppTopbar } from "../../../shell/AppTopbar";
import {
  competitionCrumbs,
  lockedNotice,
  useCompetitionReadOnly,
} from "../../../shell/CompetitionLayout";
import { OfferCard } from "./OfferCard";
import { useAuth } from "../../../auth/useAuth";
import {
  isAdmin,
  useAssignRole,
  useRevokeRoleAssignment,
  useUsers,
} from "../../../auth/queries";
import { useRuleSets } from "../../../lib/queries/rules";
import {
  useCompetition,
  useCompetitionOrganizers,
  useCompetitionSeries,
  useCreateCompetitionSeries,
  useDeleteCompetition,
  useUpdateCompetition,
  type Competition,
} from "../../../lib/queries/competitions";
import {
  CompetitionFormFields,
  canSubmitCompetitionForm,
  competitionFormPayload,
  competitionToForm,
} from "../../../components/CompetitionForm";
import { CompetitionLifecycle } from "../../../components/CompetitionLifecycle";
import { isFrozen } from "../../../lib/competitionState";
import type { ID } from "../../../lib/id";

export function CompetitionSettingsPage() {
  const { id } = useParams();
  const competitionId = id ?? "";
  const { data: competition, isLoading, error, refetch } = useCompetition(competitionId);

  const crumbs = competitionCrumbs(competitionId, competition, { label: "Nastavení" });

  if (isLoading) {
    return (
      <>
        <AppTopbar crumbs={crumbs} />
        <Page>
          <Page.Content>
            <LoadingState />
          </Page.Content>
        </Page>
      </>
    );
  }
  if (error || !competition) {
    return (
      <>
        <AppTopbar crumbs={crumbs} />
        <Page>
          <Page.Content>
            <ErrorState title="Soutěž se nepodařilo načíst" error={error} onRetry={refetch} />
          </Page.Content>
        </Page>
      </>
    );
  }

  return (
    <>
      <AppTopbar crumbs={crumbs} />
      <Page>
        <Page.Header title="Nastavení soutěže" subtitle={competition.name} />
        <Page.Content stack gap="lg">
          {/* Remount the cards when the server row changes so a save (or
              another manager's edit) re-seeds the local form state. */}
          <SettingsCards
            key={competition.updated_at.toISOString()}
            competition={competition}
          />
        </Page.Content>
      </Page>
    </>
  );
}

function SettingsCards({ competition }: { competition: Competition }) {
  const { user } = useAuth();
  const t = useT();
  const admin = isAdmin(user);

  const [form, setForm] = useState(() => competitionToForm(competition));
  const { data: ruleSets = [] } = useRuleSets();
  const { data: series = [] } = useCompetitionSeries(competition.season_id);
  const activeRuleSets = useMemo(
    () => ruleSets.filter((r) => r.status === "active" || r.id === competition.rule_set_id),
    [ruleSets, competition.rule_set_id],
  );

  const update = useUpdateCompetition(competition.id);
  const createSeries = useCreateCompetitionSeries();

  const saveBasics = async () => {
    try {
      let seriesId: ID | null =
        form.seriesId && form.seriesId !== "new" ? form.seriesId : null;
      if (form.seriesId === "new" && form.newSeriesName.trim()) {
        const created = await createSeries.mutateAsync({
          season_id: competition.season_id,
          name: form.newSeriesName.trim(),
        });
        seriesId = created.id;
        // Write the created id back: a failed save below must not
        // re-post the series into the unique-name 409 on retry.
        setForm((f) => ({ ...f, seriesId: String(created.id), newSeriesName: "" }));
      }
      await update.mutateAsync(
        competitionFormPayload(form, competition.season_id, seriesId),
      );
      notifySuccess("Nastavení soutěže bylo uloženo.");
    } catch (err) {
      notifyError(err);
    }
  };

  // Ohlášeno holds no přihláška, so the references stay as free as in a
  // draft; the server draws the same line (BeforePublish).
  const locked = competition.status !== "draft" && competition.status !== "announced";
  // Dokončeno or archived: the server refuses every change, so the form
  // is shown, not offered.
  // A competitions_viewer reads the settings the way a Dokončeno
  // competition shows them: every field there, disabled.
  const readOnly = useCompetitionReadOnly();
  const frozen = isFrozen(competition) || readOnly;

  return (
    <>
      <CompetitionLifecycle competition={competition} readOnly={readOnly} />

      <Card>
        <Card.Header title="Základní údaje" />
        <Card.Content>
          {(update.error || createSeries.error) && (
            <Alert variant="err">
              {apiErrorMessage(update.error || createSeries.error, t)}
            </Alert>
          )}
          {frozen && <Alert variant="info">{lockedNotice(competition, readOnly)}</Alert>}
          {/* A disabled fieldset disables every control inside it, the
              library's included, without a prop on each. */}
          <fieldset disabled={frozen} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
            <FormStack>
              <CompetitionFormFields
                form={form}
                onChange={setForm}
                ruleSets={activeRuleSets}
                series={series}
                lockRefs={locked}
              />
              {!frozen && (
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    variant="primary"
                    loading={update.isPending || createSeries.isPending}
                    disabled={!canSubmitCompetitionForm(form)}
                    onClick={saveBasics}
                  >
                    Uložit změny
                  </Button>
                </div>
              )}
            </FormStack>
          </fieldset>
        </Card.Content>
      </Card>

      <OfferCard competition={competition} />

      {admin && <OrganizersCard competition={competition} />}

      <DangerZoneCard competition={competition} />
    </>
  );
}

// OrganizersCard is admin-only: granting the competition-scoped role
// reuses the generic role-assignment endpoints.
function OrganizersCard({ competition }: { competition: Competition }) {
  const qc = useQueryClient();
  const { data: organizers = [] } = useCompetitionOrganizers(competition.id);
  const { data: users = [] } = useUsers();
  const [selectedUserId, setSelectedUserId] = useState("");
  const assign = useAssignRole(selectedUserId);
  const revoke = useRevokeRoleAssignment();

  const candidates = users.filter(
    (u) => u.active && !organizers.some((o) => o.user_id === u.id),
  );

  const invalidateOrganizers = () =>
    qc.invalidateQueries({ queryKey: ["competitions", "organizers", competition.id] });

  const add = async () => {
    try {
      await assign.mutateAsync({
        role: "competition_organizer",
        scope_type: "competition",
        scope_id: competition.id,
      });
      await invalidateOrganizers();
      setSelectedUserId("");
      notifySuccess("Organizátor byl přidán.");
    } catch (err) {
      notifyError(err);
    }
  };

  const remove = async (assignmentId: ID, userId: ID) => {
    try {
      await revoke.mutateAsync({ userId, assignmentId });
      await invalidateOrganizers();
      notifySuccess("Organizátor byl odebrán.");
    } catch (err) {
      notifyError(err);
    }
  };

  return (
    <Card>
      <Card.Header title="Organizátoři" />
      <Card.Content>
        <Text variant="muted">
          Organizátor soutěže může tuto soutěž řídit: upravovat nastavení, měnit stav a
          spravovat přihlášky. Roli přiděluje administrátor.
        </Text>
        <FormStack>
          {organizers.length === 0 ? (
            <Text variant="muted">Soutěž zatím nemá žádného organizátora.</Text>
          ) : (
            organizers.map((o) => (
              <div
                key={o.assignment_id}
                style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
              >
                <span>
                  {o.display_name || o.email}{" "}
                  <Text variant="muted">({o.email})</Text>
                </span>
                <Button
                  weight="ghost"
                  loading={revoke.isPending && revoke.variables?.assignmentId === o.assignment_id}
                  onClick={() => remove(o.assignment_id, o.user_id)}
                >
                  Odebrat
                </Button>
              </div>
            ))
          )}
          <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
            <div style={{ flex: 1 }}>
              <Select
                label="Přidat organizátora"
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
              >
                <option value="">— vyberte uživatele —</option>
                {candidates.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.display_name || u.email} ({u.email})
                  </option>
                ))}
              </Select>
            </div>
            <Button
              variant="primary"
              disabled={!selectedUserId}
              loading={assign.isPending}
              onClick={add}
            >
              Přidat
            </Button>
          </div>
        </FormStack>
      </Card.Content>
    </Card>
  );
}

// DangerZoneCard renders only while it has an action to offer: deleting
// a draft. Archiving lives in the lifecycle control, and an empty
// "danger zone" card is just noise.
function DangerZoneCard({ competition }: { competition: Competition }) {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const remove = useDeleteCompetition();
  const readOnly = useCompetitionReadOnly();

  if (competition.status !== "draft" || readOnly) return null;

  return (
    <Card>
      <Card.Header title="Smazání soutěže" />
      <Card.Content>
        <FormStack>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Text variant="muted">Smazat lze jen připravovanou soutěž. Akce je nevratná.</Text>
            <Button
              // Outline: this button OPENS the confirm, it is not the
              // commitment. The dialog's own „Smazat" is the solid one.
              variant="danger"
              weight="outline"
              onClick={() =>
                confirm({
                  title: "Smazat soutěž",
                  description: (
                    <>
                      Opravdu smazat soutěž <strong>{competition.name}</strong>? Akce je
                      nevratná.
                    </>
                  ),
                  confirmLabel: "Smazat",
                  variant: "danger",
                  // Inside onConfirm so a refusal keeps the dialog open and shows
                  // itself there. It used to render in the card behind the modal,
                  // where the dialog covering it hid the explanation.
                  onConfirm: async () => {
                    await remove.mutateAsync(competition.id);
                    navigate("/admin/souteze");
                  },
                })
              }
            >
              Smazat soutěž
            </Button>
          </div>
        </FormStack>
      </Card.Content>
    </Card>
  );
}
